import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import { currentLocale, t } from "../locale";
import { onMobile } from "../mobile";
import { download } from "../utils";
import { Bulma } from "../view/bulma";

/**
 * @param {DataPoint[]} data
 * @returns {[number, number]} [min, max]
 */
function getValueRange(data) {
	/**
	 * @param {number} val
	 * @param {number} step
	 * @returns {number}
	 */
	function limit(val, step) {
		const exp = Math.floor(Math.log10(val));
		const fac = 10 ** exp;
		val = val / fac;
		val = Math.floor(val);
		val = (val + step) * fac;
		return val;
	}

	const sortedValues = data.map((d) => d.value).sort((a, b) => a - b);

	let min = sortedValues[0];
	min = min < 0 ? limit(min, 0) : 0;

	let max = sortedValues[sortedValues.length - 1];
	max = limit(max, 1);

	return [min, max];
}

/**
 * @typedef {Object} ExportDeviceDetails
 * @property {string} type
 * @property {string} serialNumber
 */

/**
 * @typedef {Object} LineChartConfig
 * @property {number} [limit]
 * @property {(from: Date, to: Date, format: "csv"|"json"|"xlsx") => Promise<DataPoint[]|Blob>} getData
 * @property {string} [label]
 * @property {string} [unit]
 * @property {ExportDeviceDetails} [device]
 *
 * @typedef {Object} DataPoint
 * @property {string} timestamp
 * @property {number} value
 *
 * @param {LineChartConfig} cfg
 */
function lineChart(cfg) {
	const timeRangeSelect = Bulma.select({
		options: [
			Bulma.option(`1 ${t.day}`, "1", true),
			Bulma.option(`2 ${t.days}`, "2"),
			Bulma.option(`3 ${t.days}`, "3"),
			Bulma.option(`5 ${t.days}`, "5"),
			Bulma.option(`7 ${t.days}`, "7"),
			Bulma.option(`14 ${t.days}`, "14"),
			Bulma.option(`31 ${t.days}`, "31"),
		],
		onInput: draw,
	});

	const endDateInput = Bulma.dateInput({
		onChange: draw,
	});

	/**
	 * @returns {[Date, Date]} [from, to]
	 */
	const getDateRange = () => {
		const msPerDay = 24 * 60 * 60 * 1000;
		const to = new Date(endDateInput.getDate().getTime() + msPerDay);
		const from = new Date(
			to.getTime() - +timeRangeSelect.getValue() * msPerDay,
		);
		return [from, to];
	};

	const chartCanvas = document.createElement("canvas");
	const chartOptions = {};

	/** @type {Chart} */
	const chart = new Chart(chartCanvas, {
		type: "line",
		data: {
			labels: [],
			datasets: [],
		},
		options: {
			animation: false,
			...chartOptions,
		},
	});

	const exportFormatSelect = Bulma.select({
		options: [
			Bulma.option("CSV", "csv"),
			Bulma.option("XLSX", "xlsx", true),
		],
	});

	const content = document.createElement("div");
	content.className = "chart-content";
	content.append(
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.field(
				{},
				Bulma.label(t.device.logsPage.timeIntervalLabel),
				Bulma.control({}, timeRangeSelect.element),
			),
			Bulma.field(
				{},
				Bulma.label(t.device.logsPage.untilLabel),
				Bulma.control({}, endDateInput.element),
			),
		),
		Bulma.field(
			{
				styles: onMobile ? ["is-hidden"] : ["has-addons"],
			},
			Bulma.control({}, exportFormatSelect.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						styles: ["is-primary"],
						onClick: onClickExport,
					},
					t.export,
				),
			),
		),
		chartCanvas,
	);

	const progress = document.createElement("progress");
	progress.className = "progress is-small is-primary";
	progress.max = 100;

	const overlay = document.createElement("div");
	overlay.className = "chart-overlay is-hidden";

	const container = document.createElement("div");
	container.className = "chart-container";
	container.append(content, overlay);

	async function draw() {
		overlay.replaceChildren(progress);
		overlay.classList.remove("is-hidden");

		const [from, to] = getDateRange();
		const data = await cfg.getData(from, to, "json");
		if (data instanceof Blob) return;

		chart.data.labels = data.map((data) => data.timestamp);
		setChartDataset(data);
		setChartOptions(data, from, to);
		chart.update("none"); // Update wthout animation

		overlay.classList.add("is-hidden");
	}

	async function onClickExport() {
		const [from, to] = getDateRange();

		const format = exportFormatSelect.getValue();
		if (format !== "csv" && format !== "xlsx")
			throw "invalid export format";

		const blob = await cfg.getData(from, to, format);
		if (!(blob instanceof Blob)) throw "expected blob";

		const file = new File([blob], `export.${format}`, {
			type:
				format === "csv"
					? "text/csv"
					: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		download(file);
	}

	/**
	 * @param {DataPoint[]} data
	 */
	function setChartDataset(data) {
		const values = data.map((data) => data.value);
		const hue = getComputedStyle(document.documentElement).getPropertyValue(
			"--bulma-primary-h",
		);
		const saturation = getComputedStyle(
			document.documentElement,
		).getPropertyValue("--bulma-primary-s");
		const lightness = getComputedStyle(
			document.documentElement,
		).getPropertyValue("--bulma-primary-l");
		chart.data.datasets = [
			{
				label: cfg.unit || "?",
				type: "line",
				data: values,
				backgroundColor: `hsla(${hue}, ${saturation}, ${lightness}, 0.2)`,
				borderColor: `hsl(${hue}, ${saturation}, ${lightness})`,
				fill: true,
				spanGaps: false,
				stepped: "before",
			},
		];
	}

	/**
	 * @param {DataPoint[]} data
	 * @param {Date} from
	 * @param {Date} to
	 */
	function setChartOptions(data, from, to) {
		const [yMin, yMax] = getValueRange(data);
		chart.options = {
			responsive: true,
			maintainAspectRatio: true,
			locale: currentLocale,
			scales: {
				x: {
					display: true,
					type: "time",
					min: from.getTime(),
					max: to.getTime(),
					time: {
						tooltipFormat: "MMM d Y - HH:mm",
						unit:
							to.getTime() - from.getTime() <= 172800000
								? "hour"
								: "day",
						displayFormats: {
							hour: "HH.mm",
						},
					},
				},
				y: {
					title: {
						display: true,
						text: cfg.unit || "?",
					},
					display: true,
					min: yMin,
					max: yMax,
					ticks: {
						color: "#000000",
					},
				},
			},
			plugins: {
				legend: {
					position: "top",
					display: false,
					labels: {
						color: "#FFFFFF",
					},
				},
				tooltip: {
					enabled: true,
					yAlign: "bottom",
					callbacks: {
						/**
						 * @typedef {Object} TooltipContextDataset
						 * @property {string} [label]
						 */

						/**
						 * @typedef {Object} TooltipContextParsed
						 * @property {number | string} [x]
						 * @property {number | string} [y]
						 */

						/**
						 * @typedef {Object} TooltipContext
						 * @property {TooltipContextDataset} dataset
						 * @property {TooltipContextParsed} parsed
						 */

						/**
						 * @param {TooltipContext} context
						 */
						label: (context) => {
							let label = context.dataset.label || "";

							if (context.parsed.y !== null) {
								label = `${context.parsed.y} ${cfg.unit || "?"}`;
							}
							return label;
						},
					},
				},
			},
		};
	}

	return {
		element: container,
		mount: draw,
	};
}

export { lineChart };
